import { APIComponentAttributes } from '@ardoq/api-types';
import { add, intervalToDuration, parseDate } from '@ardoq/date-time';

const LAST_UPDATED_FIELD = 'lastUpdated';

const STALE_THRESHOLD_IN_MONTHS = 6;

const DAYS_IN_WEEK = 7;

export type LastUpdatedAge =
  | { unit: 'today' }
  | { unit: 'days' | 'weeks' | 'months' | 'years'; count: number };

export type Freshness =
  | { status: 'unknown' }
  | { status: 'known'; age: LastUpdatedAge; isStale: boolean };

export const parseLastUpdated = (
  component: APIComponentAttributes
): Date | null => {
  const lastUpdated = (component as Record<string, unknown>)[
    LAST_UPDATED_FIELD
  ];
  if (typeof lastUpdated !== 'string') return null;
  const updated = parseDate(lastUpdated);
  if (Number.isNaN(updated.getTime())) return null;
  return updated;
};

const toLargestUnitAge = (
  years: number,
  months: number,
  days: number
): LastUpdatedAge => {
  if (years > 0) return { unit: 'years', count: years };
  if (months > 0) return { unit: 'months', count: months };
  if (days >= DAYS_IN_WEEK) {
    return { unit: 'weeks', count: Math.floor(days / DAYS_IN_WEEK) };
  }
  if (days > 0) return { unit: 'days', count: days };
  return { unit: 'today' };
};

const isStale = (updated: Date, now: Date): boolean =>
  now > add(updated, { months: STALE_THRESHOLD_IN_MONTHS });

export const getFreshness = (
  component: APIComponentAttributes,
  now: Date
): Freshness => {
  const updated = parseLastUpdated(component);
  if (!updated) return { status: 'unknown' };
  if (updated >= now) {
    return { status: 'known', age: { unit: 'today' }, isStale: false };
  }

  const {
    years = 0,
    months = 0,
    days = 0,
  } = intervalToDuration({
    start: updated,
    end: now,
  });
  return {
    status: 'known',
    age: toLargestUnitAge(years, months, days),
    isStale: isStale(updated, now),
  };
};
